import { useState, useEffect, useCallback } from 'react';
import { Loader2, Filter, ChevronDown, ChevronRight, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { matchingAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

const POLL_INTERVAL_MS = 2000;

// Stage order mirrors the funnel walk in
// services/auto_matching_chain.py::auto_match_chunk_fn: transactions
// scanned -> candidate invoices considered -> survived entity filter ->
// survived direction filter -> survived amount/affinity gate -> emitted.
const STAGES = [
  { key: 'entity_filter', label: 'Entity filter', killCountKey: 'killed_entity_filter' },
  { key: 'direction_filter', label: 'Direction filter', killCountKey: 'killed_direction_filter' },
  { key: 'amount_affinity_gate', label: 'Amount / affinity gate', killCountKey: 'killed_amount_affinity_gate' },
  { key: 'below_confidence_floor', label: 'Below confidence floor', killCountKey: 'killed_below_confidence_floor' },
  { key: 'existing_pair', label: 'Already an existing pair', killCountKey: 'killed_existing_pair' },
];

// Mirrors routes/matching.py::_is_high_confidence's four eligibility checks
// for the "Confirm all high-confidence" bulk action. A pair can fail more
// than one at once (cross_currency structurally forces amount_not_exact).
const HIGH_TIER_CRITERIA = [
  { key: 'no_counterparty_signal', label: 'No counterparty signal' },
  { key: 'amount_not_exact', label: 'Amount not exact' },
  { key: 'outside_date_window', label: 'Outside date window' },
  { key: 'cross_currency', label: 'Cross currency' },
];

function SampleTable({ samples }) {
  if (!samples || samples.length === 0) {
    return <p className="text-xs text-muted-foreground pl-4 py-1">No sample kills recorded.</p>;
  }
  const columns = Object.keys(samples[0]);
  return (
    <div className="overflow-x-auto pl-4">
      <table className="w-full text-xs border">
        <thead className="bg-muted/50">
          <tr>
            {columns.map((c) => (
              <th key={c} className="text-left p-1.5">{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {samples.map((s, i) => (
            <tr key={i} className="border-t" data-testid="match-funnel-sample-row">
              {columns.map((c) => (
                <td key={c} className="p-1.5 font-mono">{String(s[c] ?? '—')}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ExcludedPrePairingByAccountTable({ rows }) {
  if (!rows || rows.length === 0) {
    return <p className="text-xs text-muted-foreground pl-4 py-1">No account breakdown recorded.</p>;
  }
  return (
    <div className="overflow-x-auto pl-4">
      <table className="w-full text-xs border">
        <thead className="bg-muted/50">
          <tr>
            <th className="text-left p-1.5">Account</th>
            <th className="text-left p-1.5">Bank</th>
            <th className="text-left p-1.5">Connection</th>
            <th className="text-left p-1.5">Raw entity field</th>
            <th className="text-left p-1.5">Txns</th>
            <th className="text-left p-1.5">Date range</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.bank_account_id} className="border-t" data-testid="match-funnel-excluded-account-row">
              <td className="p-1.5 font-mono">{r.account_name || r.bank_account_id || '—'}</td>
              <td className="p-1.5 font-mono">{r.bank_name || '—'}</td>
              <td className="p-1.5">
                {r.connection_status ? (
                  <Badge variant={r.connection_status === 'live' ? 'outline' : 'destructive'}>
                    {r.connection_status}
                  </Badge>
                ) : (
                  <span className="text-muted-foreground">n/a</span>
                )}
              </td>
              <td className="p-1.5 font-mono">{String(r.entity_field_raw ?? '—')}</td>
              <td className="p-1.5 font-mono">{r.txn_count}</td>
              <td className="p-1.5 font-mono">
                {r.min_date || '—'}{(r.min_date || r.max_date) ? ' → ' : ''}{r.max_date || '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Match Funnel is a pure point-read of the latest auto-match chain's
 * persisted stage tallies + samples (CLAUDE.md section 13) -- it never
 * recomputes or walks a collection itself. It shows the latest
 * running/complete chain's numbers with their own timestamp, live-updating
 * while that chain is still running. To generate fresh numbers, start/
 * continue the chain from the Reconciliation page's "Run Auto-Matching".
 */
export function MatchFunnelPanel({ entityId }) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set());

  const fetchFunnel = useCallback(async () => {
    try {
      const { data } = await matchingAPI.matchFunnel(entityId);
      setResult(data);
      setNotFound(false);
    } catch (err) {
      if (err?.response?.status === 404) {
        setNotFound(true);
        setResult(null);
      } else {
        toast.error(getErrorMessage(err));
      }
    }
  }, [entityId]);

  const refresh = async () => {
    setLoading(true);
    try {
      await fetchFunnel();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, [entityId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Live-update while the underlying chain is still running -- a mid-chain
  // view should keep advancing without a manual click.
  useEffect(() => {
    if (result?.chain_status !== 'running') return undefined;
    const id = setInterval(fetchFunnel, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [result?.chain_status, fetchFunnel]);

  const toggle = (key) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const stats = result && result.stats;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Filter className="h-5 w-5 text-blue-600" />
          Match Funnel (Read-Only)
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Why auto-matching emitted what it emitted, stage by stage: transactions scanned →
          candidate invoices considered → survived entity filter → survived direction filter →
          survived amount/affinity gate → emitted. Every kill stage lists up to 10 sample kills
          (transaction id, invoice id, the exact values compared). Point-read only — never
          recomputes or writes anything itself; it shows the latest auto-match chain's numbers,
          live-updating while that chain is still running. Start/continue a run from the
          Reconciliation page's "Run Auto-Matching" to generate fresh numbers.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <Button onClick={refresh} disabled={loading} size="sm" variant="outline" data-testid="match-funnel-refresh">
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
          Refresh
        </Button>

        {notFound && (
          <p className="text-sm text-muted-foreground" data-testid="match-funnel-empty">
            No auto-match chain yet for this scope — run auto-matching from the Reconciliation page first.
          </p>
        )}

        {stats && (
          <div className="space-y-3" data-testid="match-funnel-result">
            <div className="flex flex-wrap gap-2 text-xs text-muted-foreground items-center">
              <span>Computed {result.computed_at}</span>
              <span>|</span>
              <span>Scope: {result.scope}</span>
              <span>|</span>
              <Badge variant={result.chain_status === 'running' ? 'default' : result.chain_status === 'error' ? 'destructive' : 'outline'}>
                chain {result.chain_status}
              </Badge>
              {stats.inv_pool_truncated && (
                <Badge variant="destructive">
                  invoice pool truncated: {stats.candidate_invoices_considered} of {stats.inv_eligible_total}
                </Badge>
              )}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
              <div className="border rounded p-2">
                <div className="text-muted-foreground text-xs">Transactions scanned</div>
                <div className="font-mono text-lg">
                  {stats.transactions_scanned}
                  {stats.tx_eligible_total != null && <span className="text-muted-foreground text-xs"> / {stats.tx_eligible_total}</span>}
                </div>
              </div>
              <div className="border rounded p-2">
                <div className="text-muted-foreground text-xs">Candidate invoices considered</div>
                <div className="font-mono text-lg">{stats.candidate_invoices_considered}</div>
              </div>
              <div className="border rounded p-2">
                <div className="text-muted-foreground text-xs">Pairs evaluated</div>
                <div className="font-mono text-lg">{stats.pairs_evaluated}</div>
              </div>
              <div className="border rounded p-2 col-span-2 md:col-span-3">
                <div className="text-muted-foreground text-xs">Emitted (would become / became a Match)</div>
                <div className={`font-mono text-lg ${stats.emitted === 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {stats.emitted}
                </div>
              </div>
            </div>

            {stats.tx_unresolved_entity > 0 && (
              <div className="border rounded" data-testid="match-funnel-excluded-pre-pairing">
                <button
                  type="button"
                  onClick={() => toggle('excluded_pre_pairing')}
                  className="w-full flex items-center gap-1 p-2 text-left text-xs text-amber-600 hover:bg-muted/50"
                  data-testid="match-funnel-excluded-pre-pairing-toggle"
                >
                  {expanded.has('excluded_pre_pairing') ? <ChevronDown className="h-3 w-3 shrink-0" /> : <ChevronRight className="h-3 w-3 shrink-0" />}
                  <span>
                    {stats.tx_unresolved_entity} transaction(s) excluded before pairing at all: owning
                    entity could not be resolved from their bank account.
                  </span>
                </button>
                {expanded.has('excluded_pre_pairing') && (
                  <div className="p-2 pt-0 space-y-3 border-t">
                    <div>
                      <div className="text-xs font-medium mt-2 mb-1 flex items-center gap-2">
                        By account
                        {stats.excluded_pre_pairing?.skipped_malformed > 0 && (
                          <span
                            className="text-amber-600 font-normal"
                            data-testid="match-funnel-skipped-malformed"
                          >
                            ({stats.excluded_pre_pairing.skipped_malformed} row(s) skipped: malformed stored data, see server log)
                          </span>
                        )}
                      </div>
                      <ExcludedPrePairingByAccountTable rows={stats.excluded_pre_pairing?.by_account} />
                    </div>
                    <div>
                      <div className="text-xs font-medium mb-1">Sample transactions</div>
                      <SampleTable samples={stats.excluded_pre_pairing?.sample_txns} />
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="border rounded divide-y">
              {STAGES.map((stage) => {
                const killCount = stats[stage.killCountKey] || 0;
                const isOpen = expanded.has(stage.key);
                return (
                  <div key={stage.key}>
                    <button
                      type="button"
                      onClick={() => toggle(stage.key)}
                      className="w-full flex items-center justify-between p-2 text-sm hover:bg-muted/50"
                      data-testid={`match-funnel-stage-${stage.key}`}
                    >
                      <span className="flex items-center gap-1">
                        {isOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                        {stage.label}
                      </span>
                      <Badge variant={killCount > 0 ? 'outline' : 'secondary'}>
                        {killCount} killed
                      </Badge>
                    </button>
                    {isOpen && <SampleTable samples={stats.samples?.[stage.key]} />}
                  </div>
                );
              })}
            </div>

            {Object.keys(stats.skip_reason_counts || {}).length > 0 && (
              <div className="text-xs text-muted-foreground">
                Amount/affinity gate breakdown:{' '}
                {Object.entries(stats.skip_reason_counts).map(([reason, count]) => (
                  <Badge key={reason} variant="outline" className="ml-1">
                    {reason}: {count}
                  </Badge>
                ))}
              </div>
            )}

            {stats.high_tier_near_miss_counts && (
              <div className="border rounded p-3 space-y-2" data-testid="match-funnel-high-tier-near-miss">
                <div className="text-sm font-medium">High-tier near misses</div>
                <p className="text-xs text-muted-foreground">
                  Among emitted matches, how many fail each "Confirm all high-confidence" bulk-eligibility
                  criterion. A pair can fail more than one at once. Reporting only — no thresholds change here.
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                  {HIGH_TIER_CRITERIA.map((c) => (
                    <div key={c.key} className="border rounded p-2" data-testid={`match-funnel-near-miss-${c.key}`}>
                      <div className="text-muted-foreground text-xs">{c.label}</div>
                      <div className="font-mono text-lg">{stats.high_tier_near_miss_counts[c.key] || 0}</div>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => toggle('high_tier_near_miss')}
                  className="w-full flex items-center justify-between p-2 text-sm hover:bg-muted/50 border-t"
                  data-testid="match-funnel-near-miss-toggle"
                >
                  <span className="flex items-center gap-1">
                    {expanded.has('high_tier_near_miss') ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                    Sample near misses
                  </span>
                </button>
                {expanded.has('high_tier_near_miss') && <SampleTable samples={stats.samples?.high_tier_near_miss} />}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
